<template>
    <div class="grant-agreement" v-if="MonitoringForm.initialized">
        <div class="grant-agreement-box row" v-if="!showContractDetails">
            <div class="col-6">
                <h3>{{ $t('monitoring.title__grant_agreement') }} - {{ MonitoringForm.reference }}</h3>

                <p v-if="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"><b>{{ $t('monitoring.field_title__number_of_project') }}</b>: <input type="text" v-model="MonitoringForm.model.project_number" /></p>
                <p v-else><b>{{ $t('monitoring.field_title__number_of_project') }}</b>: {{ MonitoringForm.model.project_number }}</p>
                <p><b>{{ $t('monitoring.field_title__name_of_organisation') }}</b>: {{ MonitoringForm.organisation }}</p>
                <p v-if="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"><b>{{ $t('monitoring.field_title__bank_account') }}</b>: <input type="text" v-model="MonitoringForm.model.bank_account" /></p>
                <p v-else><b>{{ $t('monitoring.field_title__bank_account') }}</b>: {{ MonitoringForm.model.bank_account }}</p>
            </div>
            <div class="col-6">
                <base-button
                    type="primary"
                       class="pull-right"
                    @click.native="showContractDetails = true"
                    >
                    {{ $t('monitoring.button__view_contract_details') }}
                </base-button>
            </div>
        </div>
        <div v-else>
            <div class="grant-agreement-box row">
                <div class="col-6">
                    <h3 class="mt-2 mb-0">{{ $t('monitoring.title__grant_agreement') }} - {{ MonitoringForm.reference }}</h3>
                </div>
                <div class="col-6">
                    <base-button
                        type="primary"
                        class="pull-right"
                        @click="viewOriginalProposal"
                        >
                        {{ $t('monitoring.button__view_original_proposal') }}
                    </base-button>
                </div>
            </div>
            <card class="mt-5">
                <div class="row">
                    <div class="col-lg-8">
                        <!-- Title of the project -->
                        <div class="form-group">
                            <h4 class="card-title">{{ $t('monitoring.field_title__project_title') }}</h4>
                            <textarea
                                class="form-control"
                                name="grant_agreement.project_title"
                                v-model="MonitoringForm.model.grant_agreement.title"
                                v-validate="'required'"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                :placeholder="$t('monitoring.field_title__project_title')"
                                >
                            </textarea>
                        </div>

                        <!-- Region -->
                        <div class="form-group">
                            <h4 class="card-title">{{ $t('monitoring.field_title__region') }}</h4>
                            <select
                                id="regionSelect"
                                class="form-control"
                                name="grant_agreement.region"
                                v-model="MonitoringForm.regionId"
                                v-validate="'required'"
                                :data-vv-as="$t('monitoring.field_title__region')"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                >
                                <option :value="region.regionId" v-for="region in regions" :key="region.regionId">{{region.code}} - {{region.name}}</option>
                            </select>
                        </div>

                        <!-- Geographic scope -->
                        <div class="form-group">
                            <h4 class="card-title">{{ $t('monitoring.field_title__geographic_scope') }}</h4>
                            <textarea
                                class="form-control"
                                name="grant_agreement.project_title"
                                v-model="MonitoringForm.model.grant_agreement.geographic_scope"
                                v-validate="'required'"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                :placeholder="$t('monitoring.field_title__geographic_scope')"
                                >
                            </textarea>
                        </div>

                        <!-- Start & End date -->
                        <div class="row">
                            <!-- Starting date -->
                            <div class="col-lg-6">
                                <h4 class="card-title">{{ $t('monitoring.field_title__start_date') }}</h4>
                                <base-input
                                    name="grant_agreement.start_date"
                                    v-model="MonitoringForm.model.grant_agreement.start_date"
                                    :error="getError('grant_agreement.start_date')"
                                    v-validate="'required'"
                                    :data-vv-as="$t('monitoring.field_title__start_date')"
                                    :placeholder="$t('monitoring.field_title__start_date')"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <el-date-picker
                                        type="date"
                                        v-model="startDate"
                                        :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                        :placeholder="$t('monitoring.field_title__start_date')"
                                        >
                                    </el-date-picker>
                                </base-input>
                            </div>

                            <!-- Ending date -->
                            <div class="col-lg-6">
                                <h4 class="card-title">{{ $t('monitoring.field_title__end_date') }}</h4>
                                <base-input
                                    name="grant_agreement.end_date"
                                    v-model="MonitoringForm.model.grant_agreement.end_date"
                                    :placeholder="$t('monitoring.field_title__end_date')"
                                    :error="getError('grant_agreement.end_date')"
                                    v-validate="'required'"
                                    :data-vv-as="$t('monitoring.field_title__end_date')"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <el-date-picker
                                        type="date"
                                        v-model="endDate"
                                        :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                        :placeholder="$t('monitoring.field_title__end_date')"
                                        >
                                    </el-date-picker>
                                </base-input>
                            </div>
                        </div>

                        <!-- Duration -->
                        <div class="form-group">
                            <h4 class="card-title">{{ $t('monitoring.field_title__duration') }}</h4>
                            <textarea
                                class="form-control"
                                name="grant_agreement.duration"
                                v-model="MonitoringForm.model.grant_agreement.duration"
                                v-validate="'required'"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                :error="getError('grant_agreement.duration')"
                                :data-vv-as="$t('monitoring.field_title__duration')"
                                :label="$t('monitoring.field_title__duration')"
                                :placeholder="$t('monitoring.field_title__duration')"
                                >
                            </textarea>
                        </div>

                        <!-- Main BEST objective addressed by the project -->
                        <h4 class="card-title">{{ $t('monitoring.field_title__main_objective') }}</h4>
                        <div class="form-group">
                            <radio name="grant_agreement.main_objective"
                                value="1"
                                v-model="MonitoringForm.model.grant_agreement.main_objective"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                >
                                <label>{{ $t('monitoring.field_title__main_objective_checkbox_01') }}</label>
                            </radio>
                            <radio name="grant_agreement.main_objective"
                                value="2"
                                v-model="MonitoringForm.model.grant_agreement.main_objective"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                >
                                <label>{{ $t('monitoring.field_title__main_objective_checkbox_02') }}</label>
                            </radio>
                            <radio name="grant_agreement.main_objective"
                                value="3"
                                v-model="MonitoringForm.model.grant_agreement.main_objective"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                >
                                <label>{{ $t('monitoring.field_title__main_objective_checkbox_03') }}</label>
                            </radio>
                        </div>

                        <!-- Second BEST objective(s) -->
                        <h4 class="card-title">{{ $t('monitoring.field_title__second_objective') }}</h4>
                        <div class="form-group">
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                v-model="MonitoringForm.model.grant_agreement.second_objective_01"
                                >
                                {{ $t('monitoring.field_title__second_objective_checkbox_01') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                v-model="MonitoringForm.model.grant_agreement.second_objective_02"
                                >
                                {{ $t('monitoring.field_title__second_objective_checkbox_02') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                v-model="MonitoringForm.model.grant_agreement.second_objective_03"
                                >
                                {{ $t('monitoring.field_title__second_objective_checkbox_03') }}
                            </base-checkbox>
                        </div>

                        <!-- Requested grant amount -->
                        <div class="form-group">
                            <h4 class="card-title">{{ $t('monitoring.field_title__requested_grant_amount') }}</h4>
                            <currency-input
                                class="form-control"
                                name="grant_agreement.grant_amount"
                                v-model="MonitoringForm.model.grant_agreement.grant_amount"
                                v-validate="'required'"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                :value-range='{"min": 0, "max": 60000}'
                                currency="EUR"
                                locale="de"
                                :error="getError('grant_agreement.grant_amount')"
                                :data-vv-as="$t('monitoring.field_title__requested_grant_amount')"
                                :label="$t('monitoring.field_title__requested_grant_amount')"
                                :placeholder="$t('monitoring.field_title__requested_grant_amount')"
                            />
                        </div>

                        <!-- Project summary -->
                        <h4 class="card-title mb-0">
                            {{ $t('monitoring.field_title__project_summary') }}
                        </h4>
                        <p>{{ $t('stage_two.general_words_remaining', {words: project_summary_count}) }}</p>
                        <div class="form-group">
                            <textarea
                                class="form-control"
                                name="grant_agreement.project_summary"
                                maxwords="500"
                                v-wordscount="project_summary_count"
                                v-model="MonitoringForm.model.grant_agreement.project_summary"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                :label="$t('monitoring.field_title__project_summary')"
                                :error="getError('grant_agreement.project_summary')"
                                :placeholder="$t('general.general__insert_here')"
                                >
                            </textarea>
                        </div>

                        <!-- Main Area of intervention -->
                        <h4 class="card-title">
                            {{ $t('monitoring.field_title__main_area_of_intervention') }}
                        </h4>
                        <div class="form-group row mb-4">
                            <div class="col-5">
                                <radio
                                    name="grant_agreement.main_intervention_area"
                                    value="coastal"
                                    v-model="MonitoringForm.model.grant_agreement.main_intervention_area"
                                    :disabled="!MonitoringForm.isEditable"
                                    style="width: 100%"
                                >
                                    <label>{{ $t('monitoring.field_title__main_area_of_intervention_coastal') }}</label>
                                </radio>
                                <radio
                                    name="grant_agreement.main_intervention_area"
                                    value="marine"
                                    v-model="MonitoringForm.model.grant_agreement.main_intervention_area"
                                    :disabled="!MonitoringForm.isEditable"
                                    style="width: 100%"
                                >
                                    <label>{{ $t('monitoring.field_title__main_area_of_intervention_marine') }}</label>
                                </radio>
                                <radio
                                    name="grant_agreement.main_intervention_area"
                                    value="forest"
                                    v-model="MonitoringForm.model.grant_agreement.main_intervention_area"
                                    :disabled="!MonitoringForm.isEditable"
                                    style="width: 100%"
                                >
                                    <label>{{ $t('monitoring.field_title__main_area_of_intervention_forest') }}</label>
                                </radio>
                                <radio
                                    name="grant_agreement.main_intervention_area"
                                    value="terrestrial"
                                    v-model="MonitoringForm.model.grant_agreement.main_intervention_area"
                                    :disabled="!MonitoringForm.isEditable"
                                    style="width: 100%"
                                >
                                    <label>{{ $t('monitoring.field_title__main_area_of_intervention_terrestrial') }}</label>
                                </radio>
                                <radio
                                    name="grant_agreement.main_intervention_area"
                                    value="fresh_water"
                                    v-model="MonitoringForm.model.grant_agreement.main_intervention_area"
                                >
                                    <label>{{ $t('monitoring.field_title__main_area_of_intervention_fresh_water') }}</label>
                                </radio>
                            </div>
                            <div class="col-7">
                                <radio
                                    name="grant_agreement.main_intervention_area"
                                    value="ecosystem_services_management"
                                    v-model="MonitoringForm.model.grant_agreement.main_intervention_area"
                                >
                                    <label>{{ $t('monitoring.field_title__main_area_of_intervention_ecosystem_services_management') }}</label>
                                </radio>
                                <radio
                                    name="grant_agreement.main_intervention_area"
                                    value="species"
                                    v-model="MonitoringForm.model.grant_agreement.main_intervention_area"
                                    :disabled="!MonitoringForm.isEditable"
                                    style="width: 100%"
                                >
                                    <label>{{ $t('monitoring.field_title__main_area_of_intervention_species') }}</label>
                                </radio>
                                <radio
                                    name="grant_agreement.main_intervention_area"
                                    value="protected_areas"
                                    v-model="MonitoringForm.model.grant_agreement.main_intervention_area"
                                    :disabled="!MonitoringForm.isEditable"
                                >
                                    <label>{{ $t('monitoring.field_title__main_area_of_intervention_protected_areas') }}</label>
                                </radio>
                                <radio
                                    name="grant_agreement.main_intervention_area"
                                    value="agroforestry"
                                    v-model="MonitoringForm.model.grant_agreement.main_intervention_area"
                                    :disabled="!MonitoringForm.isEditable"
                                >
                                    <label>{{ $t('monitoring.field_title__main_area_of_intervention_agroforestry') }}</label>
                                </radio>
                                <radio
                                    name="grant_agreement.main_intervention_area"
                                    value="circular_economy"
                                    v-model="MonitoringForm.model.grant_agreement.main_intervention_area"
                                    :disabled="!MonitoringForm.isEditable"
                                >
                                    <label>{{ $t('monitoring.field_title__main_area_of_intervention_circular_economy') }}</label>
                                </radio>
                            </div>
                        </div>

                        <!-- Other Area of intervention -->
                        <h4 class="card-title">
                            {{ $t('monitoring.field_title__other_area_of_intervention') }}
                        </h4>
                        <div class="form-group row mb-4">
                            <div class="col-5">
                                <base-checkbox
                                    name="checkbox"
                                    v-model="MonitoringForm.model.grant_agreement.other_area_of_intervention_coastal"
                                    :disabled="!MonitoringForm.isEditable"
                                    >
                                    {{ $t('monitoring.field_title__other_area_of_intervention_coastal') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!MonitoringForm.isEditable"
                                    v-model="MonitoringForm.model.grant_agreement.other_area_of_intervention_marine"
                                    >
                                    {{ $t('monitoring.field_title__other_area_of_intervention_marine') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!MonitoringForm.isEditable"
                                    v-model="MonitoringForm.model.grant_agreement.other_area_of_intervention_forest"
                                    >
                                    {{ $t('monitoring.field_title__other_area_of_intervention_forest') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!MonitoringForm.isEditable"
                                    v-model="MonitoringForm.model.grant_agreement.other_area_of_intervention_terrestrial"
                                    >
                                    {{ $t('monitoring.field_title__other_area_of_intervention_terrestrial') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!MonitoringForm.isEditable"
                                    v-model="MonitoringForm.model.grant_agreement.other_area_of_intervention_fresh_water"
                                    >
                                    {{ $t('monitoring.field_title__other_area_of_intervention_fresh_water') }}
                                </base-checkbox>
                            </div>
                            <div class="col-7">
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!MonitoringForm.isEditable"
                                    v-model="MonitoringForm.model.grant_agreement.other_area_of_intervention_ecosystem_services_management"
                                    >
                                    {{ $t('monitoring.field_title__other_area_of_intervention_ecosystem_services_management') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!MonitoringForm.isEditable"
                                    v-model="MonitoringForm.model.grant_agreement.other_area_of_intervention_species"
                                    >
                                    {{ $t('monitoring.field_title__other_area_of_intervention_species') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!MonitoringForm.isEditable"
                                    v-model="MonitoringForm.model.grant_agreement.other_area_of_intervention_protected_areas"
                                    >
                                    {{ $t('monitoring.field_title__other_area_of_intervention_protected_areas') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!MonitoringForm.isEditable"
                                    v-model="MonitoringForm.model.grant_agreement.other_area_of_intervention_agroforestry"
                                    >
                                    {{ $t('monitoring.field_title__other_area_of_intervention_agroforestry') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="!MonitoringForm.isEditable"
                                    v-model="MonitoringForm.model.grant_agreement.other_area_of_intervention_area_circular_economy"
                                    >
                                    {{ $t('monitoring.field_title__other_area_of_intervention_circular_economy') }}
                                </base-checkbox>
                            </div>
                        </div>

                        <!-- Main Type of intervention -->
                        <h4 class="card-title">
                            {{ $t('monitoring.field_title__main_type_of_intervention') }}
                        </h4>
                        <div class="form-group row mb-4">
                            <div class="col-5">
                                <radio
                                    name="grant_agreement.main_type_of_intervention"
                                    value="conservation"
                                    v-model="MonitoringForm.model.grant_agreement.main_intervention_type"
                                    v-validate.persist="'required'"
                                    :disabled="!MonitoringForm.isEditable"
                                    :data-vv-as="$t('monitoring.field_title__main_type_of_intervention')"
                                >
                                    <label>{{ $t('monitoring.field_title__main_type_of_intervention_conservation') }}</label>
                                </radio>
                                <radio
                                    name="grant_agreement.main_type_of_intervention"
                                    value="restoration"
                                    v-model="MonitoringForm.model.grant_agreement.main_intervention_type"
                                    v-validate.persist="'required'"
                                    :disabled="!MonitoringForm.isEditable"
                                    :data-vv-as="$t('monitoring.field_title__main_type_of_intervention')"
                                >
                                    <label>{{ $t('monitoring.field_title__main_type_of_intervention_restoration') }}</label>
                                </radio>
                                <radio
                                    name="grant_agreement.main_type_of_intervention"
                                    value="threat_management"
                                    v-model="MonitoringForm.model.grant_agreement.main_intervention_type"
                                    v-validate.persist="'required'"
                                    :disabled="!MonitoringForm.isEditable"
                                    :data-vv-as="$t('monitoring.field_title__main_type_of_intervention')"
                                >
                                    <label>{{ $t('monitoring.field_title__main_type_of_intervention_threat_management') }}</label>
                                </radio>
                            </div>
                            <div class="col-7">
                                <radio
                                    name="grant_agreement.main_type_of_intervention"
                                    value="awareness_education"
                                    v-model="MonitoringForm.model.grant_agreement.main_intervention_type"
                                    v-validate.persist="'required'"
                                    :disabled="!MonitoringForm.isEditable"
                                    :data-vv-as="$t('monitoring.field_title__main_type_of_intervention')"
                                >
                                    <label>{{ $t('monitoring.field_title__main_type_of_intervention_awareness_education') }}</label>
                                </radio>
                                <radio
                                    name="grant_agreement.main_type_of_intervention"
                                    value="other"
                                    v-model="MonitoringForm.model.grant_agreement.main_intervention_type"
                                    v-validate.persist="'required'"
                                    :disabled="!MonitoringForm.isEditable"
                                    :data-vv-as="$t('monitoring.field_title__main_type_of_intervention')"
                                >
                                    <label>{{ $t('monitoring.field_title__main_type_of_intervention_other') }}</label>
                                </radio>
                            </div>
                            <input
                                type="text"
                                class="form-control mt-3"
                                name="grant_agreement.main_type_of_intervention_other"
                                v-if="MonitoringForm.model.grant_agreement.main_intervention_type === 'other'"
                                v-model="MonitoringForm.model.grant_agreement.main_intervention_type_other"
                                v-validate.persist="'required'"
                                :data-vv-as="$t('monitoring.field_title__main_type_of_intervention_other')"
                                :disabled="!MonitoringForm.isEditable"
                                />
                            <label v-if="getError('grant_agreement.main_intervention_type_other')" class="error text-danger">{{ getError('grant_agreement.main_intervention_type_other') }}</label>
                        </div>
                        <label v-if="getError('grant_agreement.main_intervention_type')" class="error text-danger">{{ getError('grant_agreement.main_intervention_type') }}</label>

                        <!-- Other Type of intervention -->
                        <h4 class="card-title">
                            {{ $t('monitoring.field_title__other_type_of_intervention') }}
                        </h4>
                        <div class="form-group row mb-4">
                            <div class="col-5">
                                <base-checkbox
                                    name="checkbox"
                                    v-model="MonitoringForm.model.grant_agreement.other_type_of_intervention_conservation"
                                    :disabled="!MonitoringForm.isEditable"
                                    >
                                    {{ $t('monitoring.field_title__other_type_of_intervention_conservation') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    v-model="MonitoringForm.model.grant_agreement.other_type_of_intervention_restoration"
                                    :disabled="!MonitoringForm.isEditable"
                                    >
                                    {{ $t('monitoring.field_title__other_type_of_intervention_restoration') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    v-model="MonitoringForm.model.grant_agreement.other_type_of_intervention_threat_management"
                                    :disabled="!MonitoringForm.isEditable"
                                    >
                                    {{ $t('monitoring.field_title__other_type_of_intervention_threat_management') }}
                                </base-checkbox>
                            </div>
                            <div class="col-7">
                                <base-checkbox
                                    name="checkbox"
                                    v-model="MonitoringForm.model.grant_agreement.other_type_of_intervention_awareness_education"
                                    :disabled="!MonitoringForm.isEditable"
                                    >
                                    {{ $t('monitoring.field_title__other_type_of_intervention_awareness_education') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    v-model="MonitoringForm.model.grant_agreement.other_type_of_intervention_other"
                                    :disabled="!MonitoringForm.isEditable"
                                    >
                                    {{ $t('monitoring.field_title__other_type_of_intervention_other') }}
                                </base-checkbox>
                            </div>
                            <input
                                type="text"
                                class="form-control mt-3"
                                name="grant_agreement.other_type_of_intervention_other_text"
                                v-if="MonitoringForm.model.grant_agreement.other_type_of_intervention_other"
                                v-model="MonitoringForm.model.grant_agreement.other_type_of_intervention_other_text"
                                v-validate.persist="'required'"
                                :data-vv-as="$t('monitoring.field_title__other_type_of_intervention_other')"
                                :disabled="!MonitoringForm.isEditable"
                                />
                            <label v-if="getError('grant_agreement.other_type_of_intervention_other_text')" class="error text-danger">{{ getError('grant_agreement.other_type_of_intervention_other_text') }}</label>
                        </div>

                    </div>

                    <MonitoringGrantAgreementsQuestions
                        v-bind:loggedUser="loggedUser"
                        v-bind:UserRoles="UserRoles"
                        v-bind:MonitoringForm="MonitoringForm"
                    />
                </div>
            </card>
            <div class="row mb-5">
                <div class="col-12">
                    <card class="mt-2">
                        <div class="row">
                            <div class="col-lg-5">
                                <!-- Reporting interval -->
                                <h4>{{ $t('monitoring.field_title__reporting_interval') }}</h4>
                                <h5 class="float-left mr-5">{{ $t('monitoring.field_subtitle__reporting_interval') }}</h5>
                                <div class="form-group">
                                    <radio name="model.reporting_interval"
                                        class="form-check-inline"
                                        value="2"
                                        v-model="MonitoringForm.model.grant_agreement.reporting_interval"
                                        :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                        >
                                        <label>2</label>
                                    </radio>
                                    <radio name="model.reporting_interval"
                                        class="form-check-inline"
                                        value="3"
                                        v-model="MonitoringForm.model.grant_agreement.reporting_interval"
                                        :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                        >
                                        <label>3</label>
                                    </radio>
                                </div>
                                <div class="form-group">
                                    <!-- Period (n) -->
                                    <div class="row" v-for="(index) in +MonitoringForm.model.grant_agreement.reporting_interval" :key="index">
                                        <div class="col-lg-3 d-flex justify-content-center align-items-center">
                                            <h4>Period {{ index }}</h4>
                                        </div>
                                        <div class="col-lg-9">
                                            <!-- Start date -->
                                            <div class="row">
                                                <div class="col-4">
                                                    <h4>{{ $t('monitoring.field_title__start_date') }}</h4>
                                                </div>
                                                <div class="col-8">
                                                    <base-input
                                                        :name="'grant_agreement.interval_start_date_' + index"
                                                        v-model="MonitoringForm.model.grant_agreement['reporting_interval_period_' + index + '_start_date']"
                                                        :error="getError('grant_agreement.start_date')"
                                                        v-validate="'required'"
                                                        :data-vv-as="$t('monitoring.field_title__start_date')"
                                                        :placeholder="$t('monitoring.field_title__start_date')"
                                                        :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                                        >
                                                        <el-date-picker
                                                            type="date"
                                                            v-model="intervals['reportingIntervalStartDate' + index]"
                                                            :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                                            :placeholder="$t('monitoring.field_title__start_date')"
                                                            >
                                                        </el-date-picker>
                                                    </base-input>
                                                </div>
                                            </div>

                                            <!-- End date -->
                                            <div class="row">
                                                <div class="col-4">
                                                    <h4>{{ $t('monitoring.field_title__end_date') }}</h4>
                                                </div>
                                                <div class="col-8">
                                                    <base-input
                                                        :name="'grant_agreement.interval_end_date_' + index"
                                                        v-model="MonitoringForm.model.grant_agreement['reporting_interval_period_' + index + '_end_date']"
                                                        :error="getError('grant_agreement.end_date')"
                                                        v-validate="'required'"
                                                        :data-vv-as="$t('monitoring.field_title__end_date')"
                                                        :placeholder="$t('monitoring.field_title__end_date')"
                                                        :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                                        >
                                                        <el-date-picker
                                                            type="date"
                                                            v-model="intervals['reportingIntervalEndDate' + index]"
                                                            :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                                            :placeholder="$t('monitoring.field_title__end_date')"
                                                            >
                                                        </el-date-picker>
                                                    </base-input>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </card>

                    <div class="grant-agreement-box row mt-5">
                        <div class="col-12">
                            <div class="form-group file-group">
                                <span class="file-caption">{{ $t('monitoring.box_title__signed_contract') }}</span>
                                <upload-file
                                    v-if="MonitoringForm.initialized"
                                    v-model="MonitoringForm.files.grant_agreement.signed_contract"
                                    class="upload-file"
                                    section="grant_agreement.signed_contract"
                                    select-text="Admin Upload Contract"
                                    :file-data="MonitoringForm.files.grant_agreement.signed_contract"
                                    :is-editable="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                                    :model-name="'Monitoring'"
                                >
                                </upload-file>
                            </div>
                        </div>
                    </div>

                    <div class="ammendment-box mt-3 row" v-for="(amend, index) in MonitoringForm.model.grant_agreement.contract_amends" :key="'contract_' + index">
                        <div class="col-12">
                            <div class="form-group file-group">
                                <span class="file-caption">{{ $t('monitoring.box_title__amend_request', [index + 1]) }}</span>
                                <upload-file
                                    v-if="MonitoringForm.initialized"
                                    v-model="MonitoringForm.files.grant_agreement['amend_' + index + '_signed_contract']"
                                    class="upload-file"
                                    :select-text="$t('monitoring.button_title_upload_ammended_contract')"
                                    :section="'grant_agreement.amend_' + index + '_signed_contract'"
                                    :file-data="MonitoringForm.files.grant_agreement['amend_' + index + '_signed_contract']"
                                    :is-editable="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                                    :model-name="'Monitoring'"
                                >
                                </upload-file>
                            </div>
                            <div class="clearance-bar">
                                <label>{{ $t('monitoring.title_clearance_box') }}</label>
                                <radio name="model.info_risk_assessment"
                                    class="form-check-inline"
                                    value="cleared-with-condition"
                                    v-model="amend.clearance"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <label>{{ $t('monitoring.field_title_cleared_under_conditions') }}</label>
                                </radio>
                                <radio name="model.info_risk_assessment"
                                    class="form-check-inline"
                                    value="cleared"
                                    v-model="amend.clearance"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <label>{{ $t('monitoring.field_title_cleared') }}</label>
                                </radio>
                                <radio name="model.info_risk_assessment"
                                    class="form-check-inline"
                                    value="not-cleared"
                                    v-model="amend.clearance"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <label>{{ $t('monitoring.field_title_not_cleared') }}</label>
                                </radio>
                            </div>
                            <comment-box
                                class="form-group"
                                v-model="amend.comments"
                                :is-editable="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                                :comments="amend.comments"
                                :pull-right="false">
                            </comment-box>
                        </div>
                    </div>

                    <base-button
                        v-if="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                        class="mt-4"
                        type="primary"
                        @click="addContractAmmendment"
                        >
                        {{ $t('monitoring.button_title_add_new_ammendment') }}
                    </base-button>
                </div>
            </div>
            <div class="row mb-5">
                <div class="col-12">
                    <div class="grant-agreement-box row mt-5">
                        <div class="col-12">
                            <div class="form-group file-group">
                                <span class="file-caption">{{ $t('monitoring.field_title_signed_budget') }}</span>
                                <upload-file
                                    v-if="MonitoringForm.initialized"
                                    v-model="MonitoringForm.files.grant_agreement.signed_budget"
                                    class="upload-file"
                                    section="grant_agreement.signed_budget"
                                    :select-text="$t('monitoring.button_title_admin_upload_budget')"
                                    :file-data="MonitoringForm.files.grant_agreement.signed_budget"
                                    :is-editable="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                                    :model-name="'Monitoring'"
                                >
                                </upload-file>
                            </div>
                        </div>
                    </div>

                    <div class="ammendment-box mt-3 row" v-for="(amend, index) in MonitoringForm.model.grant_agreement.budget_amends" :key="'contract_' + index">
                        <div class="col-12">
                            <div class="form-group file-group">
                                <span class="file-caption">{{ $t('monitoring.box_title__amend_request', [index + 1]) }}</span>
                                <upload-file
                                    v-if="MonitoringForm.initialized"
                                    v-model="MonitoringForm.files.grant_agreement['amend_' + index + '_signed_budget']"
                                    class="upload-file"
                                    :select-text="$t('monitoring.button_title_upload_ammended')"
                                    :section="'grant_agreement.amend_' + index + '_signed_budget'"
                                    :file-data="MonitoringForm.files.grant_agreement['amend_' + index + '_signed_budget']"
                                    :is-editable="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                                    :model-name="'Monitoring'"
                                >
                                </upload-file>
                            </div>
                            <div class="clearance-bar">
                                <label>{{ $t('monitoring.title_clearance_box') }}</label>
                                <radio name="model.info_risk_assessment"
                                    class="form-check-inline"
                                    value="cleared-with-condition"
                                    v-model="amend.clearance"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <label>{{ $t('monitoring.field_title_cleared_under_conditions') }}</label>
                                </radio>
                                <radio name="model.info_risk_assessment"
                                    class="form-check-inline"
                                    value="cleared"
                                    v-model="amend.clearance"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <label>{{ $t('monitoring.field_title_cleared') }}</label>
                                </radio>
                                <radio name="model.info_risk_assessment"
                                    class="form-check-inline"
                                    value="not-cleared"
                                    v-model="amend.clearance"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <label>{{ $t('monitoring.field_title_not_cleared') }}</label>
                                </radio>
                            </div>
                            <comment-box
                                class="form-group"
                                v-model="amend.comments"
                                :is-editable="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                                :comments="amend.comments"
                                :pull-right="false">
                            </comment-box>
                        </div>
                    </div>

                    <base-button
                        v-if="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                        class="mt-4"
                        type="primary"
                        @click="addBudgetAmmendment"
                        >
                        {{ $t('monitoring.button_title_add_new_ammendment') }}
                    </base-button>
                </div>
            </div>
        </div>

        <div class="row mt-5" v-if="MonitoringForm.initialized && MonitoringForm.isEditable">
            <div class="col-12">
                <base-button type="default" wide @click.native="saveProgress">{{$t('general.save_progress')}}</base-button>
                <base-button class="pull-right" wide @click.native="submit">{{$t('general.submit')}}</base-button>
            </div>
        </div>
    </div>
</template>
<script>
    import {MonitoringForm, RegionService, UserRoles} from '@/services';
    import {DatePicker} from 'element-ui';
    import Multiselect from 'vue-multiselect';
    import {CommentBox} from 'src/components';
    import {EventBus} from '@/event-bus.js';

    import MonitoringGrantAgreementsQuestions from "../../MonitoringGrantAgreementsQuestions"

    export default
    {
        components:
        {
            MonitoringForm,
            [DatePicker.name]: DatePicker,
            Multiselect,
            CommentBox,
            MonitoringGrantAgreementsQuestions
        },
        data()
        {
            return {
                UserRoles,
                MonitoringForm,
                loggedUser: this.$store.getters['Auth/loggedUser'],
                showContractDetails: false,
                startDate: '',
                endDate: '',
                intervals: {
                    reportingIntervalStartDate1: '',
                    reportingIntervalEndDate1: '',
                    reportingIntervalStartDate2: '',
                    reportingIntervalEndDate2: '',
                    reportingIntervalStartDate3: '',
                    reportingIntervalEndDate3: '',
                },
                moment: '',
                regions: [],
                project_summary_count: 500,
            }
        },
        computed:
        {
            countriesByRegion()
            {
                let countries = [];
                let region = this.regions.filter((region) =>
                {
                    return region.regionId === MonitoringForm.regionId;
                });

                if (region[0] !== undefined && region[0].countries !== undefined && region[0].countries.length)
                {
                    countries = region[0].countries;
                }

                return countries;
            },
            reportingIntervalStartDate1()
            {
                return this.intervals.reportingIntervalStartDate1;
            },
            reportingIntervalStartDate2()
            {
                return this.intervals.reportingIntervalStartDate2;
            },
            reportingIntervalStartDate3()
            {
                return this.intervals.reportingIntervalStartDate3;
            },
            reportingIntervalEndDate1()
            {
                return this.intervals.reportingIntervalEndDate1;
            },
            reportingIntervalEndDate2()
            {
                return this.intervals.reportingIntervalEndDate2;
            },
            reportingIntervalEndDate3()
            {
                return this.intervals.reportingIntervalEndDate3;
            },
        },
        async beforeMount()
        {
            const vm = this;
            const loader = vm.$loading.show();

            // vm.loggedUser = vm.$store.getters['Auth/loggedUser'];

            vm.moment = require('moment');

            RegionService.getRegions().then((response) =>
            {
                vm.regions = response.data;

                loader.hide();
            });

            if (!MonitoringForm.model.grant_agreement.contract_amends)
            {
                MonitoringForm.model.grant_agreement.contract_amends = [];
            }

            if (!MonitoringForm.model.grant_agreement.budget_amends)
            {
                MonitoringForm.model.grant_agreement.budget_amends = [];
            }

            vm.startDate = MonitoringForm.model.grant_agreement.start_date || '';
            vm.endDate = MonitoringForm.model.grant_agreement.end_date || '';

            vm.intervals.reportingIntervalStartDate1 = MonitoringForm.model.grant_agreement['reporting_interval_period_1_start_date'] || '';
            vm.intervals.reportingIntervalEndDate1 = MonitoringForm.model.grant_agreement['reporting_interval_period_1_end_date'] || '';
            vm.intervals.reportingIntervalStartDate2 = MonitoringForm.model.grant_agreement['reporting_interval_period_2_start_date'] || '';
            vm.intervals.reportingIntervalEndDate2 = MonitoringForm.model.grant_agreement['reporting_interval_period_2_end_date'] || '';
            vm.intervals.reportingIntervalStartDate3 = MonitoringForm.model.grant_agreement['reporting_interval_period_3_start_date'] || '';
            vm.intervals.reportingIntervalEndDate3 = MonitoringForm.model.grant_agreement['reporting_interval_period_3_end_date'] || '';

        },
        async beforeCreate()
        {
            const vm = this;

            vm.loggedUser = vm.$store.getters['Auth/loggedUser'];

            vm.$forceUpdate();

        },
        methods:
        {
            addContractAmmendment()
            {
                MonitoringForm.model.grant_agreement.contract_amends.push({});

                this.$forceUpdate();
            },

            addBudgetAmmendment()
            {
                MonitoringForm.model.grant_agreement.budget_amends.push({});

                this.$forceUpdate();
            },

            getError(field)
            {
                return this.errors.first(field);
            },

            validate()
            {
                return this.$validator.validateAll().then(res =>
                {
                    this.$emit('on-validated', res, this.model);

                    return res;
                });
            },

            viewOriginalProposal()
            {
                let path = '/proposals/fill/' + MonitoringForm.callId + '/' + MonitoringForm.proposalId;
                window.open(path, '_blank');
            },

            saveProgress()
            {
                EventBus.$emit('monitoring-save-progress');
            },

            submit()
            {
                EventBus.$emit('monitoring-submit');
            },
        },
        watch:
        {
            startDate(new_val)
            {
                const date = this.moment(new_val).format('YYYY-MM-DD');
                MonitoringForm.model.grant_agreement.start_date = (date === 'Invalid date' ? null : date);
            },
            endDate(new_val)
            {
                const date = this.moment(new_val).format('YYYY-MM-DD');
                MonitoringForm.model.grant_agreement.end_date = (date === 'Invalid date' ? null : date);
            },
            reportingIntervalStartDate1(new_val)
            {
                const date = this.moment(new_val).format('YYYY-MM-DD');
                MonitoringForm.model.grant_agreement['reporting_interval_period_1_start_date'] = (date === 'Invalid date' ? null : date);
            },
            reportingIntervalEndDate1(new_val)
            {
                const date = this.moment(new_val).format('YYYY-MM-DD');
                MonitoringForm.model.grant_agreement['reporting_interval_period_1_end_date'] = (date === 'Invalid date' ? null : date);
            },
            reportingIntervalStartDate2(new_val)
            {
                const date = this.moment(new_val).format('YYYY-MM-DD');
                MonitoringForm.model.grant_agreement['reporting_interval_period_2_start_date'] = (date === 'Invalid date' ? null : date);
            },
            reportingIntervalEndDate2(new_val)
            {
                const date = this.moment(new_val).format('YYYY-MM-DD');
                MonitoringForm.model.grant_agreement['reporting_interval_period_2_end_date'] = (date === 'Invalid date' ? null : date);
            },
            reportingIntervalStartDate3(new_val)
            {
                const date = this.moment(new_val).format('YYYY-MM-DD');
                MonitoringForm.model.grant_agreement['reporting_interval_period_3_start_date'] = (date === 'Invalid date' ? null : date);
            },
            reportingIntervalEndDate3(new_val)
            {
                const date = this.moment(new_val).format('YYYY-MM-DD');
                MonitoringForm.model.grant_agreement['reporting_interval_period_3_end_date'] = (date === 'Invalid date' ? null : date);
            },
        }
    };
</script>
<style lang="scss" scoped>
.grant-agreement
{
    counter-reset: title_counter;
    // counter-reset: subtitle_counter;

    .card-title
    {
        counter-increment: title_counter;
        counter-reset: subtitle_counter;

        &:before
        {
            content: counter(title_counter) '. '
        }
    }

    .card-subtitle
    {
        margin: 10px 0;
        counter-increment: subtitle_counter;

        &:before
        {
            content: counter(title_counter) '.' counter(subtitle_counter) '. '
        }
    }
}
</style>
